<template>
    <div v-if="loaded">
        <section class="my-4 mx-4">
            <div class="border border-gray-light p-4 rounded-md text-center shadow">
                <small class="uppercase">
                    Team Joining Link
                </small>
                <p class="text-xl font-bold">{{ team.joining_link }}</p>
                <a href class="text-xs text-primary" @click.prevent="copyJoiningLink">
                    <font-awesome-icon :icon="['fas', 'copy']"></font-awesome-icon>
                    <span>{{ copyText }}</span>
                </a>
            </div>

            <div>
                <section class="mt-4">
                    <h3 class="p-4 text-lg font-bold flex items-baseline justify-between">
                        Basics
                        <button class="text-sm font-bold text-primary" @click="editTeamDetails">
                            <font-awesome-icon :icon="['fas', 'pen']"></font-awesome-icon>
                            Edit
                        </button>
                    </h3>

                    <div class="p-4 border border-gray-dark rounded">
                        <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                            <div class="sm:col-span-1">
                                <dt class="text-sm font-bold text-gray-darker">
                                Name
                                </dt>
                                <dd class="mt-1">
                                    {{ displayingTeam.name }}
                                </dd>
                            </div>
                            <div class="sm:col-span-1">
                                <dt class="text-sm font-bold text-gray-darker">
                                Email
                                </dt>
                                <dd class="mt-1">
                                    {{ displayingTeam.email }}
                                </dd>
                            </div>
                            <div class="sm:col-span-2">
                                <dt class="text-sm font-bold text-gray-darker">
                                    Billing Details
                                </dt>
                                <!-- This needs to be in the same line because we are preserving the linebreaks using the whitespace-pre-line class. -->
                                <dd class="mt-1 whitespace-pre-line">{{ displayingTeam.profile || '&lt;empty&gt;' }}</dd>
                            </div>
                        </dl>
                    </div>
                </section>
            </div>
        </section>
        <section class="mt-4">
            <h3 class="mx-4 p-4 text-lg font-bold flex items-baseline justify-between">
                        Members
                        <button class="text-sm font-bold text-primary" @click="addMember">
                            <font-awesome-icon :icon="['fas', 'plus']"></font-awesome-icon>
                            Add
                        </button>
                    </h3>
            <table class="w-full table-fixed">
                <thead>
                    <tr class="border border-gray-light bg-gray-light">
                        <th class="w-9/12 uppercase text-xs text-left px-8 py-3">Member</th>
                        <th class="w-2/12 hidden md:table-cell uppercase text-xs text-right px-4 py-3">Joined On</th>
                        <th class="w-1/12 text-xs text-right px-4 py-3"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="border border-bottom border-gray-light" v-for="user in users" :key="user.id">
                        <td class="px-8 py-2">
                            <p>{{ user.name }} <span class="uppercase bg-gray-dark rounded text-xs px-2 py-1" v-if="user.role !== 'member'">{{ user.role }}</span></p>
                            <p class="text-gray-darker text-sm">{{ user.email }}</p>
                        </td>
                        <td class="hidden md:table-cell px-4 py-2 text-right">{{ user.joined_team_on }}</td>
                        <td class="px-2 py-2 text-center"><a href @click.prevent="manageMember(user)" v-if="currentUser.id !== user.id"><font-awesome-icon :icon="['fas', 'pen']" class="text-gray-darker text-sm"></font-awesome-icon></a></td>
                    </tr>
                </tbody>
            </table>
        </section>

        <section class="m-4 py-4 border border-destructive rounded">
            <h3 class="p-4 text-lg font-bold text-destructive">
                Delete
            </h3>
            <div class="mx-4">
                <p class="">
                    This action deletes this team and removes all members (<strong>{{ users.length }} total</strong>) from the team.
                </p>
                <delete-team-button :team="team" class="mt-4 py-2 px-4 rounded-md bg-destructive text-white font-bold"></delete-team-button>
            </div>
        </section>
    </div>
    <div v-else>
        <loading />
    </div>
</template>

<script>
import AddTeamMemberModal from './AddTeamMemberModal.vue';
import EditTeamModal from './EditTeamModal.vue';
import ManageTeamMemberModal from './ManageTeamMemberModal.vue';

const TEXT_COPY_DEFAULT = 'COPY';
const TEXT_COPY_SUCCESS = 'COPIED!';

export default {
    props: {
        team: {
            type    : Object,
            required: true,
        }
    },
    data() {
        return {
            loaded        : false,
            copyText      : TEXT_COPY_DEFAULT,
            users         : [],
            currentUser   : window.User,
            displayingTeam: {
                name    : this.team.name,
                email   : this.team.email,
                profile : this.team.profile,
            },
        }
    },
    mounted() {
        this.reloadTeamDetails('users');
    },
    methods: {
        reloadTeamDetails(relations = '') {
            this.loaded = false;
            axios.get(`/api/teams/${this.team.id}?with=${relations}`)
                .then(response => {
                    this.displayingTeam.name    = response.data.data.name;
                    this.displayingTeam.email   = response.data.data.email;
                    this.displayingTeam.profile = response.data.data.profile;

                    if (relations != '') {
                        this.users = response.data.data.users;
                    }

                    this.loaded = true;
                })
                .catch(console.error);
        },
        copyJoiningLink() {
            this.$copyText(this.team.joining_link).then(() => {
                this.copyText = TEXT_COPY_SUCCESS;
                
                // back to default text after some time
                setTimeout(() => {
                    this.copyText = TEXT_COPY_DEFAULT;
                }, 3000);
            });
        },
        editTeamDetails() {
            Modal.show(EditTeamModal, {propsData: {
                team: this.team,
            }})
            .then(() => {
                this.reloadTeamDetails();
            })
            .catch(console.error);
        },
        addMember() {
            Modal.show(AddTeamMemberModal, {propsData: {
                team: this.team,
            }})
            .then(() => {
                this.reloadTeamDetails('users');
            })
            .catch(console.error);
        },
        manageMember(user) {
            Modal.show(ManageTeamMemberModal, {propsData: {
                team: this.team,
                user: user,
            }}).then(response => {
                this.reloadTeamDetails('users');
            });
        },
    }

}
</script>